import { staffManualApprovedProposalPreviewPath } from "@shared/manualApprovedProposalWrapper";
import { proposalQuotationPdfApiPath } from "@shared/proposalPdfFilename";
import { isDtoolsJsonProposalSourceValue, isManualPdfUploadProposalSourceValue } from "@shared/proposalSources";

/** Full retail deal workspace (timeline, line items, notes, actions). */
export function staffProposalDealWorkspacePath(proposalId: number): string {
  return `/sales/proposals/${proposalId}/deal`;
}

/** Staff Link A — internal preview (works for draft proposals). */
export function staffProposalPreviewPath(proposalId: number): string {
  return `/sales/proposals/${proposalId}/preview`;
}

/** Staff detail view for manual PDF uploads (metadata + attached PDF only). */
export function staffManualUploadedProposalPath(proposalId: number): string {
  return `/sales/proposals/${proposalId}/manual`;
}

export function staffProposalListDestinationPath(row: {
  id: number;
}): string {
  return staffProposalDealWorkspacePath(row.id);
}

/**
 * Staff navigation when opening the “primary” proposal view (wrapper vs internal preview).
 * Post-upload save navigates to {@link staffProposalDealWorkspacePath} instead — do not use this for that flow.
 */
export function staffPrimaryProposalViewPath(proposal: {
  id: number;
  projectId?: number | null;
  proposalSource?: string | null;
  /** Ignored for routing — presence of originalProposalStorageKey is authoritative (see below). */
  hasUploadedOriginalPdf?: boolean | null;
  originalProposalStorageKey?: string | null;
}): string {
  if (isDtoolsJsonProposalSourceValue(proposal.proposalSource)) {
    return staffProposalPreviewPath(proposal.id);
  }
  const key = String(proposal.originalProposalStorageKey ?? "").trim();
  const pid = proposal.projectId != null ? Number(proposal.projectId) : NaN;
  if (key.length > 0 && Number.isFinite(pid) && pid > 0) {
    return `/proposal-wrapper/${pid}/${proposal.id}`;
  }
  if (key.length > 0 && isManualPdfUploadProposalSourceValue(proposal.proposalSource)) {
    return staffManualApprovedProposalPreviewPath(proposal.id);
  }
  return staffProposalPreviewPath(proposal.id);
}

/** Query flag: staff Link A preview should auto-open the quotation PDF (legacy blob path / manual uploads). */
export const STAFF_VIEW_PDF_QUERY = "viewPdf";
/** Value that opens the generated PDF in the browser viewer (no automatic download). */
export const STAFF_VIEW_PDF_VALUE = "view";

/**
 * Path for Deal Workspace “View PDF”.
 * D-Tools / retail quotations use the server pdfkit endpoint; manual uploaded PDFs keep the preview auto-open flow.
 */
export function staffProposalViewPdfPath(proposal: {
  id: number;
  projectId?: number | null;
  proposalSource?: string | null;
  hasUploadedOriginalPdf?: boolean | null;
  originalProposalStorageKey?: string | null;
}): string {
  const key = String(proposal.originalProposalStorageKey ?? "").trim();
  const isManualUpload =
    key.length > 0 && !isDtoolsJsonProposalSourceValue(proposal.proposalSource);
  if (isManualUpload) {
    const path = staffPrimaryProposalViewPath(proposal);
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}${STAFF_VIEW_PDF_QUERY}=${STAFF_VIEW_PDF_VALUE}`;
  }
  return proposalQuotationPdfApiPath(proposal.id);
}

export function isStaffViewPdfRequested(search: string): boolean {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  const value = new URLSearchParams(raw).get(STAFF_VIEW_PDF_QUERY);
  // Accept legacy `viewPdf=1` as well as `viewPdf=view`.
  return value === STAFF_VIEW_PDF_VALUE || value === "1" || value === "open";
}

/** Remove `viewPdf` from the current URL without a navigation. */
export function clearStaffViewPdfQueryFromLocation(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(STAFF_VIEW_PDF_QUERY)) return;
  url.searchParams.delete(STAFF_VIEW_PDF_QUERY);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", next);
}
